import { Accessor, batch, createSignal } from 'solid-js';

/** Per-section hooks the panel uses to apply selection and bulk show/hide. */
export interface SectionSelectionDelegate {
  ownsNode(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  isNodeDisabled?(nodeId: string): boolean;
  isNodeLoading?(nodeId: string): boolean;
}

/** Handle a section uses to report its selection and unregister. */
export interface SectionSelectionHandle {
  applySelection(selectedIds: string[]): void;
  dispose(): void;
}

export interface ProjectDataSelectionOptions {
  /** Whether a multi-select modifier (cmd/ctrl) is held */
  isAdditive?: Accessor<boolean>;
  /** Whether a range-select modifier (shift) is held */
  isRange?: Accessor<boolean>;
}

export interface ProjectDataSelection {
  selectedIds: Accessor<ReadonlySet<string>>;
  isNodeSelected(nodeId: string): boolean;
  setSelection(selectedIds: string[]): void;
  clearSelection(): void;
  registerSection(delegate: SectionSelectionDelegate): SectionSelectionHandle;
  onVisibilityToggle(nodeId: string): void;
}

/**
 * Panel-wide selection shared by every tree. A tree only knows about its own selection;
 * this manager merges each tree's selection into one set
 */
export const createProjectDataSelection = (
  options: ProjectDataSelectionOptions = {}
): ProjectDataSelection => {
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<string>>(
    new Set<string>()
  );
  const sections = new Set<SectionSelectionDelegate>();

  const isNodeSelected = (nodeId: string): boolean => selectedIds().has(nodeId);

  const setSelection = (ids: string[]): void => {
    setSelectedIds(new Set(ids));
  };

  const clearSelection = (): void => {
    setSelectedIds(new Set<string>());
  };

  const registerSection = (
    delegate: SectionSelectionDelegate
  ): SectionSelectionHandle => {
    sections.add(delegate);

    const applySelection = (ids: string[]): void => {
      setSelectedIds((prev) => {
        const previousSectionIds = [...prev].filter((id) =>
          delegate.ownsNode(id)
        );
        const isAdditive = options.isAdditive?.() ?? false;
        const isRange = options.isRange?.() ?? false;
        const isDeselectWithinSection =
          !isRange &&
          ids.length < previousSectionIds.length &&
          ids.every((id) => previousSectionIds.includes(id));

        if (!isAdditive && !isDeselectWithinSection) {
          return new Set(ids);
        }

        // Keep other sections' ids; replace this section's with the incoming set.
        const next = new Set<string>();
        for (const id of prev) {
          if (!delegate.ownsNode(id)) next.add(id);
        }
        for (const id of ids) next.add(id);
        return next;
      });
    };

    return {
      applySelection,
      dispose: () => {
        sections.delete(delegate);
      },
    };
  };

  const ownerOf = (nodeId: string): SectionSelectionDelegate | undefined => {
    for (const section of sections) {
      if (section.ownsNode(nodeId)) return section;
    }
    return undefined;
  };

  const onVisibilityToggle = (nodeId: string): void => {
    const owner = ownerOf(nodeId);
    if (!owner) return;

    const selected = selectedIds();
    if (selected.size > 1 && selected.has(nodeId)) {
      const target = !owner.isNodeVisible(nodeId);
      batch(() => {
        selected.forEach((id) => {
          const section = ownerOf(id);
          if (!section) return;
          if (section.isNodeDisabled?.(id) || section.isNodeLoading?.(id))
            return;
          if (section.isNodeVisible(id) !== target) {
            section.onVisibilityToggle(id);
          }
        });
      });
    } else {
      owner.onVisibilityToggle(nodeId);
    }
  };

  return {
    selectedIds,
    isNodeSelected,
    setSelection,
    clearSelection,
    registerSection,
    onVisibilityToggle,
  };
};
